import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';

import {
  GAEvent,
  getGAViewPromotion,
  getGAViewItemsFromProductCarousel,
} from 'utils/gtag';
import { IState } from 'store';
import {
  IShowCaseState,
  loadShowCaseData,
  updateFilters,
} from 'store/modules/showcase';

import Modal from 'components/common/Modal';
import Grid from 'components/common/Templates/Grid';
import { Pagination } from 'components/common/Pagination';
import { SimpleSelect } from 'components/common/Select/SimpleSelect';

import { ShowcaseAside } from './ShowcaseAside';

import * as S from './styles';

interface IShowcase {
  permaLink?: string;
  term?: string;
}

export function Showcase({ permaLink, term }: IShowcase) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { showcase, filters } = useSelector<IState, IShowCaseState>(
    state => state.showcase,
  );

  const [page, setPage] = useState(1);
  const [quantityItems, setQuantityItems] = useState(28);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  const sizeOptions = useMemo(() => {
    if (!showcase.opcoesTamanho) return [];
    const sizeOptionsEdited = showcase.opcoesTamanho.map(item => {
      return { ...item, label: item.text };
    });
    return sizeOptionsEdited;
  }, [showcase.opcoesTamanho]);

  const sortingOptions = useMemo(() => {
    if (!showcase.opcoesOrdenacao) return [];
    const sizeOptionsEdited = showcase.opcoesOrdenacao.map(item => {
      return { ...item, label: item.text };
    });
    return sizeOptionsEdited;
  }, [showcase.opcoesOrdenacao]);

  const sortingOptionsSelected = useMemo(() => {
    if (!sortingOptions.length) return null;
    return sortingOptions.find(item => item.selected);
  }, [sortingOptions]);

  useEffect(() => {
    const hasPrevious = page > 1;
    const hasNext = page < showcase.vitrine.length;

    setHasNextPage(hasNext);
    setHasPreviousPage(hasPrevious);
  }, [showcase, page]);

  useEffect(() => {
    if (showcase.vitrine.length) {
      const campanha = Cookies.get('@SportsNutrition:campanha');
      if (campanha) {
        showcase.vitrine.forEach((product, index) => {
          GAEvent({
            action: 'view_promotion',
            data: getGAViewPromotion({
              campanha: JSON.parse(campanha),
              product,
              index,
            }),
          });
        });

        Cookies.set('@SportsNutrition:itemList', showcase.titulo);
      } else {
        GAEvent({
          action: 'view_item_list',
          data: getGAViewItemsFromProductCarousel({
            products: showcase.vitrine,
            itemListId: 'RESULTADO DE BUSCA',
            itemListName: 'RESULTADO DE BUSCA',
          }),
        });

        Cookies.set('@SportsNutrition:itemList', 'RESULTADO DE BUSCA');
      }
    }
  }, [showcase]);

  useEffect(() => {
    if (filters.pagina) {
      dispatch(
        loadShowCaseData({
          permaLink,
          routeParams: { ...filters, pagina: page },
        }),
      );

      dispatch(
        updateFilters({
          pagina: page,
        }),
      );
    }
  }, [dispatch, page]);

  const handleSelectSortingOption = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (optionSelected: any) => {
      dispatch(
        loadShowCaseData({
          permaLink,
          routeParams: { ...filters, ord: optionSelected.value, pagina: page },
        }),
      );

      dispatch(
        updateFilters({
          ord: optionSelected.value,
          pagina: page,
        }),
      );
    },
    [filters, dispatch, permaLink],
  );

  const handleLoadingNewProducts = useCallback(() => {
    if (filters.pagina) {
      dispatch(
        loadShowCaseData({
          permaLink,
          routeParams: { ...filters, pagina: filters.pagina + 1 },
        }),
      );

      dispatch(
        updateFilters({
          pagina: filters.pagina + 1,
        }),
      );
    }
  }, [dispatch, filters, permaLink]);

  return (
    <S.Container className="showcase">
      <div className="filters" onClick={() => setShowMenuModal(true)}>
        <img src="/images/icons/filter-white.svg" alt="Filtro" />
        Filtros avançados
      </div>

      {showMenuModal && (
        <div className="modal-filters-showcase">
          <Modal onClose={() => setShowMenuModal(false)} withScroll>
            <ShowcaseAside permaLink={permaLink} page={page} />
          </Modal>
        </div>
      )}

      <S.SubHeader className="sub-header">
        {router.asPath.includes('busca') ? (
          <h1 className="search-slug">{`${router.query.slug} com Ofertas Incríveis`}</h1>
        ) : router.asPath.includes('promocoes/') ? (
          <h1>Promoção {showcase.titulo}</h1>
        ) : router.asPath.includes('promocoes') ? (
          <h1>Promoções</h1>
        ) : (
          <h1>{showcase.titulo}</h1>
        )}

        <div className="filters-by">
          <div className="filter-classify-by">
            <p>Ordenar por:</p>

            <SimpleSelect
              name="classifyBy"
              value={sortingOptionsSelected}
              options={sortingOptions}
              handleChange={handleSelectSortingOption}
            />
          </div>
        </div>
      </S.SubHeader>

      <S.Content className="showcase-content">
        {(showcase.filtros.marcas.length > 0 ||
          showcase.filtros.categorias.length > 0 ||
          showcase.filtros.categoriasArvore.length > 0) && (
          <ShowcaseAside permaLink={permaLink} page={page} />
        )}

        {!showcase.vitrine.length ? (
          <S.EmptyList>
            <p>No momento esta categoria não possui produtos disponíveis.</p>
          </S.EmptyList>
        ) : (
          <div className="showcase-list-items">
            <InfiniteScroll
              dataLength={showcase.vitrine.length}
              next={() => handleLoadingNewProducts()}
              hasMore={showcase.hasMore}
              loader={
                <h4
                  style={{
                    textAlign: 'center',
                    marginTop: '2rem',
                    fontWeight: 400,
                  }}
                >
                  Carregando mais produtos...
                </h4>
              }
              initialScrollY={0}
            >
              <Grid items={showcase.vitrine} />
            </InfiniteScroll>

            {/* <Pagination
              onPageChange={setPage}
              pageIndex={page}
              totalPages={showcase.totalPaginas}
              totalCount={showcase.totalRegistros}
              resultsPerPage={quantityItems}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
            /> */}
          </div>
        )}
      </S.Content>
    </S.Container>
  );
}
